import {
  createContext,
  useCallback,
  useContext,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { EnvironmentContext } from '../environment_context';
import { THEMES } from './themes';
import type { Theme } from './tokens';
import type { ThemeController } from './theme_controller';

const ThemeContext = createContext<Theme | null>(null);

export interface ThemeProviderProps {
  controller: ThemeController;
  children: ReactNode;
}

/**
 * Resolves the active theme from the controller's setting and provides it to
 * the tree. An `auto` setting follows the terminal appearance detected at
 * startup (`EnvironmentContext.theme`).
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ controller, children }) => {
  const { theme: detectedAppearance } = useContext(EnvironmentContext);

  const subscribe = useCallback(
    (listener: () => void) => controller.subscribe(listener),
    [controller],
  );
  const getSetting = useCallback(() => controller.getSetting(), [controller]);
  const setting = useSyncExternalStore(subscribe, getSetting);

  const theme = THEMES[setting === 'auto' ? detectedAppearance : setting];

  return <ThemeContext value={theme}>{children}</ThemeContext>;
};

/**
 * The resolved theme for the current render. Falls back to the detected
 * terminal appearance when no ThemeProvider is mounted (e.g. in component
 * tests that only provide an EnvironmentContext).
 */
export function useTheme(): Theme {
  const theme = useContext(ThemeContext);
  const { theme: detectedAppearance } = useContext(EnvironmentContext);
  return theme ?? THEMES[detectedAppearance];
}
